import * as React from 'react';
import {
  Award,
  CheckCircle2,
  Gift,
  Medal,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {LoyaltyIndexProps} from './LoyaltyIndex.type';
import {StatusCard} from './_components/StatusCard';
import {RulesTable} from './_components/RulesTable';
import {BadgesList} from './_components/BadgesList';
import {ReportsCard} from './_components/ReportsCard';

function Breadcrumb({accountCode}: {accountCode: string}) {
  return (
    <nav className="loyalty-breadcrumb" aria-label="Breadcrumb">
      <a
        href={`/${accountCode}/admin/`}
        className="loyalty-breadcrumb__link">
        Dashboard
      </a>
      <span className="loyalty-breadcrumb__separator">/</span>
      <span>Fidelidade</span>
    </nav>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint: string;
}

function MetricCard({icon, label, value, hint}: MetricCardProps) {
  return (
    <article className="loyalty-metric">
      <div className="loyalty-metric__header">
        <div className="loyalty-metric__copy">
          <p className="loyalty-eyebrow">{label}</p>
          <p className="loyalty-metric__value">{value}</p>
        </div>
        <div className="loyalty-icon-box" aria-hidden="true">
          {icon}
        </div>
      </div>
      <p className="loyalty-muted loyalty-metric__hint">{hint}</p>
    </article>
  );
}

export default function LoyaltyIndex({
  account,
  loyaltyRules,
  loyaltyBadges,
  loyaltyGlobalEnabled,
  paths,
}: LoyaltyIndexProps) {
  const activeRules = loyaltyRules.filter((rule) => rule.status === 'active');
  const draftRules = loyaltyRules.filter((rule) => rule.status === 'draft');
  const activeBadges = loyaltyBadges.filter((badge) => badge.status === 'active');
  const rewardTypes = Array.from(
    new Set(
      loyaltyRules
        .map((rule) => rule.reward?.type)
        .filter((type): type is string => Boolean(type))
    )
  );
  const readyForMembers =
    loyaltyGlobalEnabled && account.loyaltyEnabled && activeRules.length > 0;
  const readinessScore = [
    loyaltyGlobalEnabled,
    account.loyaltyEnabled,
    activeRules.length > 0,
  ].filter(Boolean).length;

  return (
    <section className="loyalty-page">
      <Breadcrumb accountCode={account.code} />

      <header className="loyalty-hero">
        <div className="loyalty-hero__content">
          <div className="loyalty-chip">
            <Sparkles size={16} />
            Fidelidade
          </div>
          <h1 className="loyalty-title">Programa de Fidelidade</h1>
          <p className="loyalty-hero__description">
            Configure regras, badges e acompanhe recompensas concedidas aos
            alunos em um painel único.
          </p>
        </div>
        <div className="loyalty-actions">
          <a
            href={paths.settings}
            className="loyalty-button loyalty-button--secondary"
          >
            <Settings size={18} />
            Configurar
          </a>
          <a
            href={paths.newRule}
            className="loyalty-button loyalty-button--primary"
          >
            <Plus size={18} />
            Nova regra
          </a>
        </div>
      </header>

      <div className="loyalty-metrics-grid">
        <MetricCard
          icon={<ShieldCheck size={22} />}
          label="Prontidão"
          value={readyForMembers ? 'Sim' : 'Não'}
          hint={
            readyForMembers
              ? 'Sistema global, conta e regra ativa.'
              : `${readinessScore}/3 requisitos concluídos.`
          }
        />
        <MetricCard
          icon={<CheckCircle2 size={22} />}
          label="Regras ativas"
          value={activeRules.length}
          hint={`${draftRules.length} em rascunho para revisar.`}
        />
        <MetricCard
          icon={<Medal size={22} />}
          label="Badges ativos"
          value={activeBadges.length}
          hint={`${loyaltyBadges.length} badges cadastrados no total.`}
        />
        <MetricCard
          icon={<Award size={22} />}
          label="Tipos de recompensa"
          value={rewardTypes.length}
          hint={
            rewardTypes.length > 0
              ? 'Pontos, badges ou acessos configurados.'
              : 'Nenhuma recompensa configurada.'
          }
        />
      </div>

      <div className="loyalty-section">
        <StatusCard
          account={account}
          loyaltyGlobalEnabled={loyaltyGlobalEnabled}
          settingsPath={paths.settings}
          activeRulesCount={activeRules.length}
          readinessScore={readinessScore}
        />
      </div>

      <div className="loyalty-main-grid">
        <div className="loyalty-main-grid__primary">
          <RulesTable
            rules={loyaltyRules}
            paths={paths}
            accountCode={account.code}
          />
        </div>
        <aside className="loyalty-main-grid__aside">
          <BadgesList
            badges={loyaltyBadges}
            paths={paths}
            accountCode={account.code}
          />
          <ReportsCard
            rewardsPath={paths.rewards}
            rulesCount={loyaltyRules.length}
            badgesCount={loyaltyBadges.length}
          />
        </aside>
      </div>
    </section>
  );
}
